import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { palette } from 'styled-tools';

import { minWidth } from '../../utils/mediaQueries';
import { units } from '../../utils/spacing';
import { FontWeight, fontWeight, FontWeights } from '../../utils/typography';
import Button from '../Button';
import { ButtonIcon } from '../Button/styles';
import { ButtonSize, ButtonSizes, OptionalBackgroundButtonProps } from '../Button/types';
import { PaletteColor } from '../ThemeProvider';

interface NewButtonProps {
	isSelected: boolean;
	color1: string;
	color2?: string | undefined;
	contentAlignment: 'left' | 'center';
	$borderColor: string;
	$hasIcon: boolean;
	$marginBottom?: string;
	$weight?: FontWeight;
}

// Adapted from `../Button/styles`, but adding 1px additional padding in the unselected, no-gradient case
// so it stays consistent with the 2px-width border for the selected case
function getPadding(
	size: ButtonSize,
	hasIconAndText: boolean,
	isSelected: boolean,
	isGradientButton: boolean,
): FlattenSimpleInterpolation {
	const additionalPadding = isSelected || isGradientButton ? '0px' : '1px';
	switch (size) {
		case ButtonSizes.Sm:
			return css`
				padding: calc(${2 / 3}rem + ${additionalPadding}) calc(${8 / 9}rem + ${additionalPadding});
				${hasIconAndText ? 'padding-left: calc(1rem + ${additionalPadding});' : ''}
			`;
		case ButtonSizes.Md:
			return css`
				padding: calc(${3 / 4}rem + ${additionalPadding}) calc(${10 / 9}rem + ${additionalPadding});
				${hasIconAndText ? `padding-left: calc(${67 / 54}rem + ${additionalPadding});` : ''}

				${minWidth('mobileLarge')} {
					padding-top: calc(${7 / 8}rem + ${additionalPadding});
					padding-bottom: calc(${7 / 8}rem + ${additionalPadding});
				}
			`;
		case ButtonSizes.Lg:
			return css`
				padding: calc(1rem + ${additionalPadding}) calc(${4 / 3}rem + ${additionalPadding});
				${hasIconAndText ? `padding-left: calc(${79 / 54}rem + ${additionalPadding});` : ''}
			`;
	}
}

export const Wrapper = styled(Button).attrs({ textColor: 'text' })<
	NewButtonProps & OptionalBackgroundButtonProps
>`
	${p => fontWeight(p.$weight ?? FontWeights.Medium)};
	margin-bottom: ${p => p.$marginBottom ?? units(1)};
	border: ${p => (p.$borderColor ? `1px ${p.$borderColor} solid` : 'none')};
	${props =>
		getPadding(props.size ?? ButtonSizes.Md, props.$hasIcon, props.isSelected, Boolean(props.color2))};
	transition: padding 0s;
	${props => {
		if (props.isSelected) {
			if (props.color2) {
				return css`
					background: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)) padding-box,
						linear-gradient(-225deg, ${props.color1}, ${props.color2}) border-box;
					border: 2px solid transparent;
					color: ${palette('white')};
				`;
			}
			return css`
				border-width: 2px;
			`;
		}
		if (props.color2) {
			return css`
				background: linear-gradient(${palette('white')}, ${palette('white')}) padding-box,
					linear-gradient(-225deg, ${props.color1}, ${props.color2}) border-box;
				border: 2px solid transparent;
			`;
		}
	}}
`;

export const IconWrapper = styled(ButtonIcon)<{
	$isSelected: boolean;
	$color: PaletteColor;
	$gradientId: string;
}>`
	svg {
		& path {
			fill: ${props => (props.$isSelected ? palette(props.$color) : `url(#${props.$gradientId})`)};
		}
	}
`;
